<script setup>
import { computed } from 'vue'

const props = defineProps({
  method: {
    type: Object,
    required: true
  },
  selected: {
    type: Boolean,
    default: false
  },
  size: {
    type: String,
    default: 'large', // 'large' or 'small'
    validator: (value) => ['large', 'small'].includes(value)
  }
})

const emit = defineEmits(['toggle'])

const badges = computed(() => {
  return props.method.metadata?.badges || []
})

const cardClasses = computed(() => {
  const base = 'border-2 rounded-2xl text-left transition-all duration-200 group cursor-pointer'
  const size = props.size === 'large' ? 'p-6' : 'p-5'
  const selected = props.selected
    ? `border-2 shadow-md bg-blue-50`
    : 'border-gray-200 bg-white hover:border-brand-blue hover:shadow-lg'

  return `${base} ${size} ${selected}`
})

const iconBgClasses = computed(() => {
  const size = props.size === 'large' ? 'w-16 h-16' : 'w-14 h-14'
  const transition = props.selected ? '' : 'group-hover:brightness-110'
  return `${size} rounded-2xl flex items-center justify-center transition-all ${transition}`
})

const iconSize = computed(() => {
  return props.size === 'large' ? 'text-2xl' : 'text-xl'
})

const handleToggle = () => {
  emit('toggle', props.method.id)
}
</script>

<template>
  <button
    type="button"
    :class="cardClasses"
    :style="{ borderColor: selected ? method.color : '' }"
    @click="handleToggle"
  >
    <div class="flex items-start justify-between mb-4">
      <div
        :class="iconBgClasses"
        :style="{ backgroundColor: `${method.color}20` }"
      >
        <span
          class="material-symbols-outlined"
          :class="iconSize"
          :style="{ color: method.color }"
        >
          {{ method.icon_name }}
        </span>
      </div>

      <div class="w-7 h-7 border-2 rounded-full flex items-center justify-center transition-colors"
        :class="selected ? 'border-brand-blue bg-brand-blue' : 'border-gray-300'"
      >
        <span v-if="selected" class="material-symbols-outlined text-white text-sm">check</span>
        <span v-else class="material-symbols-outlined text-gray-400 text-sm">add</span>
      </div>
    </div>

    <h3 class="text-lg font-bold text-gray-900 mb-2">{{ method.name }}</h3>
    <p class="text-sm text-gray-600 mb-4">{{ method.description }}</p>

    <div v-if="badges.length > 0" class="flex items-center flex-wrap gap-3">
      <span
        v-for="(badge, index) in badges"
        :key="index"
        class="flex items-center text-xs px-3 py-1 rounded-full transition-colors"
        :class="selected ? 'text-gray-700 bg-blue-100' : 'text-gray-500 bg-gray-100'"
      >
        <span class="material-symbols-outlined text-sm mr-1.5">{{ badge.icon }}</span>
        {{ badge.label }}
      </span>
    </div>
  </button>
</template>
